/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Pagination from 'components/Pagination/Pagination';

import styles from './Result.module.scss';

/**
 * This is the Result component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Result({
  content,
  root,
  loading,
  data,
  showInitial,
  onPaginate,
  currentPage,
  labels,
  itemWidth,
  defaultTitle,
  view,
}) {
  const [initiallyCleared, setInitiallyCleared] = useState(false);
  const [initialContent, setInitialContent] = useState(null);
  const portalRoot = document.querySelector(root);
  const refResult = useRef();

  if (portalRoot && !initiallyCleared) {
    // Save initial content for later
    setInitialContent(portalRoot.innerHTML);

    // Clear portal contents, to show loaded results
    portalRoot.innerHTML = '';
    setInitiallyCleared(true);
  }

  const renderInitial = () => {
    return <div dangerouslySetInnerHTML={{ __html: initialContent }} />;
  };

  const renderEntries = () => {
    const paginatedTitle =
      data && data.pagination
        ? data.pagination.totalResults > 1
          ? `${data.pagination.totalResults} ${labels.more_results}`
          : data.pagination.totalResults === 1
          ? labels.one_result
          : labels.no_results
        : '&nbsp;';

    //const title = defaultTitle ? defaultTitle : paginatedTitle;
    const title =
      data && data.items.length && defaultTitle ? defaultTitle : null;

    return (
      <div className="entrylist__main">
        {title && <h2 className="entrylist__heading">{title}</h2>}
        {data && (
          <div className="entrylist__list">
            <ul
              className={classNames('inner-grid', {
                'entrylist__item-width-3': itemWidth === 3,
                'entrylist__item-width-4': itemWidth === 4,
                'entrylist__item-width-6': itemWidth === 6,
              })}
            >
              {data.items &&
                data.items.map((item, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
            </ul>
            {data.pagination && data.pagination.totalPages > 1 && (
              <Pagination
                current={currentPage}
                total={data.pagination.totalPages}
                onClick={onPaginate}
                labels={labels}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStudies = () => {
    return (
      <section className="simplelist-studies__main">
        {data && (
          <>
            <ul>
              {data.items &&
                data.items.map((item, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
            </ul>
            {data.pagination && data.pagination.totalPages > 1 && (
              <Pagination
                current={currentPage}
                total={data.pagination.totalPages}
                onClick={onPaginate}
                labels={labels}
              />
            )}
          </>
        )}
      </section>
    );
  };

  const renderArchive = () => {
    return (
      <>
        {data && data.groups && (
          <>
            {data.groups.map((group) => (
              <>
                <section className="simple-list__main">
                  <h2 className="content-width">{group.title}</h2>
                  <ul className="simple-list__list">
                    {group.items.map((item, index) => (
                      <li
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                </section>
              </>
            ))}
            {data.pagination && data.pagination.totalPages > 1 && (
              <Pagination
                current={currentPage}
                total={data.pagination.totalPages}
                onClick={onPaginate}
                labels={labels}
              />
            )}
          </>
        )}
      </>
    );
  };

  const renderItems = () => {
    switch (view) {
      case 'studies':
        return renderStudies();

      case 'archive':
        return renderArchive();

      case 'entries':
      default:
        return renderEntries();
    }
  };

  const renderResult = () => {
    return (
      <div
        ref={refResult}
        className={classNames(styles.content, {
          [styles.contentLoading]: loading,
        })}
      >
        {showInitial && renderInitial()}
        {!showInitial && renderItems()}
      </div>
    );
  };

  useEffect(() => {
    if (loading && data) {
      refResult.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [loading]);

  if (portalRoot) {
    return ReactDOM.createPortal(renderResult(), portalRoot);
  }

  return renderResult();
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Result.propTypes = {
  content: PropTypes.string.isRequired,
  root: PropTypes.string,
  data: PropTypes.object,
  loading: PropTypes.bool,
  showInitial: PropTypes.bool,
  onPaginate: PropTypes.func,
  currentPage: PropTypes.number,
  itemWidth: PropTypes.oneOf([3, 4, 6]),
  defaultTitle: PropTypes.string,
  view: PropTypes.oneOf(['entries', 'studies', 'archive']),
  labels: PropTypes.shape({
    no_results: PropTypes.string,
    one_result: PropTypes.string,
    more_results: PropTypes.string,
    updating: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Result.defaultProps = {
  data: null,
  root: '',
  loading: false,
  showInitial: true,
  onPaginate: () => {},
  currentPage: 1,
  itemWidth: 3,
  defaultTitle: null,
  view: 'entries',
  labels: {
    no_results: 'Ingen resultater',
    one_result: '1 resultat',
    more_results: ' resultater',
    updating: 'Oppdaterer',
  },
};

// export default React.memo(Result);
export default Result;
